import React from 'react';

import {
  Box, Button, DialogActions, DialogContent, DialogTitle, Divider, IconButton,
  Paper, Stack, Typography,
} from '@mui/material';

import { alpha } from '@mui/material/styles';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import AppDialog from '../../../shared/components/AppDialog';

import { EMPTY_VALUE, formatTonne } from '../../../shared/utils/number';

import { MONO_FONT } from '../../../theme/muiTheme';

import { CLIMATE_ACTION_COLORS } from '../model/climateActionColors';

import {
  ACTION_PLAN_CATEGORY_LABELS, ACTION_PLAN_CONTRIBUTION_TYPES,
  ACTION_PLAN_SCOPE_LABELS,
} from './actionPlanModel';

import { deliveryStatusLabelKey, resultStatusLabelKey } from '../model/actionStatusModel';

import EvidenceUploadList from './EvidenceUploadList';

function DetailItem({ label, mono = false, value }) {
  const displayValue = value === null || value === undefined || value === '' ? EMPTY_VALUE : value;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography color='text.secondary' sx={{ fontSize: 12, fontWeight: 700 }}>{label}</Typography>
      {React.isValidElement(displayValue) ? (
        <Box sx={{ mt: 0.6 }}>{displayValue}</Box>
      ) : (
        <Typography sx={{
          fontFamily: mono ? MONO_FONT : 'inherit', fontSize: 14, fontWeight: 650,
          mt: 0.35, overflowWrap: 'anywhere',
        }}>
          {displayValue}
        </Typography>
      )}
    </Box>
  );
}

const PLAN_STATUS_TONES = {
  DRAFT: 'default',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

const RESULT_STATUS_TONES = {
  DRAFT: 'warning',
  VERIFIED: 'success',
  REJECTED: 'error',
};

function StatusChip({ label, status, kind = 'result' }) {
  const tones = kind === 'plan' ? PLAN_STATUS_TONES : RESULT_STATUS_TONES;
  const tone = tones[status] || 'default';
  return (
    <Box
      component='span'
      sx={{
        border: 1,
        borderColor: `${tone}.main`,
        borderRadius: 1,
        color: `${tone}.main`,
        display: 'inline-block',
        fontSize: 12,
        fontWeight: 700,
        px: 1,
        py: 0.35,
      }}
    >
      {label}
    </Box>
  );
}

function Section({ children, title }) {
  return (
    <Paper variant='outlined' sx={{ p: 2.25 }}>
      <Typography component='h3' sx={{ fontSize: 17, fontWeight: 700 }}>{title}</Typography>
      <Box sx={{ mt: 1.75 }}>{children}</Box>
    </Paper>
  );
}

/** Read-only summary of one action plan scoped to the selected fiscal year. */
export default function ActionPlanDetailDialog({ open, onClose, plan, selectedYear, t }) {
  if (!plan) return null;

  const fiscalYear = Number(selectedYear);
  const yearEstimate = (plan.planningEstimates || []).find(
    estimate => Number(estimate.fiscalYear) === fiscalYear,
  ) || null;
  const yearResult = (plan.annualResults || []).find(
    result => Number(result.fiscalYear) === fiscalYear,
  ) || null;

  const isCredit = plan.actionType === 'CARBON_CREDIT_RETIREMENT';
  const isOtherBenefit = plan.accountingClassification === 'OTHER_CLIMATE_IMPACT';
  const contributionLabel = ACTION_PLAN_CONTRIBUTION_TYPES.find(
    option => option.value === (isOtherBenefit ? 'OTHER_CLIMATE_BENEFIT' : 'EMISSIONS_REDUCTION'),
  )?.label;
  const scopeLabel = isCredit
    ? t('climateAction.scope.notApplicable', { defaultValue: 'Not applicable' })
    : (ACTION_PLAN_SCOPE_LABELS[plan.scope] || plan.scope);
  const categoryLabel = isCredit
    ? t('climateAction.scope.notApplicable', { defaultValue: 'Not applicable' })
    : (ACTION_PLAN_CATEGORY_LABELS[plan.category] || plan.category);
  const methodLabel = yearResult?.inputMode === 'ACTIVITY_BASED'
    ? t('climateAction.plan.dialog.activityBased')
    : t('climateAction.plan.dialog.directTco2e');

  return (
    <AppDialog fullWidth maxWidth="md" onClose={onClose} open={open}>
      <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', fontSize: 22, fontWeight: 700, p: 2, pr: 7 }}>
        {t('climateAction.actionDetail.viewTitle', { defaultValue: 'Climate Action Details' })}
        <IconButton aria-label={t('common.close')} onClick={onClose} sx={{ position: 'absolute', right: 18, top: 18 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2.25 }}>
        <Stack spacing={2}>
          <Paper variant='outlined' sx={{ bgcolor: theme => alpha(theme.palette.mode === 'dark' ? CLIMATE_ACTION_COLORS.inventoryDark : CLIMATE_ACTION_COLORS.inventory, 0.045), borderColor: 'divider', p: 2.25 }}>
            <Typography component="h3" sx={{ fontSize: 20, fontWeight: 700, overflowWrap: 'anywhere' }}>
              {plan.title}
            </Typography>
            {plan.description && (
              <Typography color='text.secondary' sx={{ fontSize: 14, lineHeight: 1.6, mt: 0.75 }}>
                {plan.description}
              </Typography>
            )}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
              <DetailItem label={t('climateAction.plan.dialog.scope')} value={scopeLabel} />
              <DetailItem label={t('climateAction.plan.dialog.category')} value={categoryLabel} />
              {!isCredit && plan.subCategory && (
                <DetailItem label={t('climateAction.plan.dialog.subCategory')} value={plan.subCategory} />
              )}
              {!isCredit && (
                <DetailItem label={t('climateAction.plan.dialog.contributionType')} value={contributionLabel} />
              )}
              <DetailItem label={t('climateAction.plan.dialog.boundary')} value={plan.boundary?.name} />
              <DetailItem
                label={t('climateAction.plan.period')}
                value={`${plan.startFiscalYear} – ${plan.endFiscalYear}`}
              />
              <DetailItem
                label={t('climateAction.plan.statusLabel')}
                value={(
                  <StatusChip
                    label={t(deliveryStatusLabelKey(plan.status))}
                    status={plan.status}
                    kind="plan"
                  />
                )}
              />
            </Box>
          </Paper>

          <Section title={`${t('climateAction.plan.expectedReduction')} · ${fiscalYear}`}>
            {yearEstimate ? (
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' } }}>
                <DetailItem
                  label={t('climateAction.cost.expectedReduction', { defaultValue: 'Expected CO₂ reduction' })}
                  mono
                  value={yearEstimate.reductionTco2ePerYear == null
                    ? EMPTY_VALUE
                    : formatTonne(yearEstimate.reductionTco2ePerYear, { withUnit: true })}
                />
                <DetailItem
                  label={t('climateAction.plan.estimatedActivity')}
                  mono
                  value={yearEstimate.estimatedActivity == null
                    ? EMPTY_VALUE
                    : `${formatTonne(yearEstimate.estimatedActivity, { withUnit: false })} ${yearEstimate.activityUnit || ''}`.trim()}
                />
                <DetailItem label={t('climateAction.plan.basisNote')} value={yearEstimate.basisNote} />
              </Box>
            ) : (
              <Typography color='text.secondary' sx={{ fontSize: 13 }}>
                {t('climateAction.plan.dialog.emptyEstimateForYear', {
                  defaultValue: 'No expected estimate recorded for {{year}}.',
                  year: fiscalYear,
                })}
              </Typography>
            )}
          </Section>

          <Section title={`${t('climateAction.plan.dialog.results')} · ${fiscalYear}`}>
            {yearResult ? (
              <>
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                  <DetailItem
                    label={t('climateAction.plan.dialog.resultValue')}
                    mono
                    value={yearResult.reportedTco2e == null
                      ? EMPTY_VALUE
                      : formatTonne(yearResult.reportedTco2e, { withUnit: true })}
                  />
                  <DetailItem
                    label={t('climateAction.plan.dialog.resultStatusLabel')}
                    value={(
                      <StatusChip
                        label={t(resultStatusLabelKey(yearResult.status), { defaultValue: yearResult.status })}
                        status={yearResult.status}
                      />
                    )}
                  />
                  <DetailItem label={t('climateAction.plan.dialog.resultMethod')} value={methodLabel} />
                  {yearResult.basisNote && (
                    <DetailItem label={t('climateAction.plan.dialog.resultNote')} value={yearResult.basisNote} />
                  )}
                </Box>
                {(yearResult.evidenceFiles || []).length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography component='h4' sx={{ fontSize: 15, fontWeight: 700 }}>
                      {t('climateAction.plan.dialog.evidenceFiles', { defaultValue: 'Evidence files' })}
                    </Typography>
                    <EvidenceUploadList
                      evidenceRecords={yearResult.evidenceFiles || []}
                      fiscalYear={fiscalYear}
                      readOnly
                      t={t}
                    />
                  </>
                )}
              </>
            ) : (
              <Typography color='text.secondary' sx={{ fontSize: 13 }}>
                {t('climateAction.plan.dialog.emptyResultForYear', {
                  defaultValue: 'No annual result recorded for {{year}}.',
                  year: fiscalYear,
                })}
              </Typography>
            )}
          </Section>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color="inherit" onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </AppDialog>
  );
}